import React, { useState } from 'react';
import { Plus, LayoutDashboard } from 'lucide-react';
import { Button } from './ui/button';
import { useBoards } from '../context/BoardContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from './ui/select';
import { TIERS } from '../lib/categories';
import { toast } from '../hooks/use-toast';

const EmptyBoard = () => {
  const { addBoard, currentUserEmail } = useBoards();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    owner_email: currentUserEmail || '',
    tier: 'team',
  });
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    setForm((f) => ({ ...f, owner_email: currentUserEmail || f.owner_email }));
  }, [currentUserEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.owner_email) {
      toast({ title: 'Missing fields', description: 'Name and owner email required' });
      return;
    }
    try {
      setSubmitting(true);
      await addBoard(form);
      toast({ title: 'Board created' });
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto py-24 px-6 text-center">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-sky-50 border border-sky-200 flex items-center justify-center mb-6">
        <LayoutDashboard className="h-8 w-8 text-[#0090D8]" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">
        Welcome to Genba Pulse
      </h2>
      <p className="text-slate-600 mb-8">
        Create your first SQPDCHI board to start tracking Safety, Quality, Productivity,
        Delivery, Cost, Human Development and Improvement.
      </p>
      <Button
        onClick={() => setOpen(true)}
        className="bg-[#0090D8] hover:bg-[#0079B5] text-white h-11 px-6"
      >
        <Plus className="h-4 w-4 mr-2" />
        Create your first board
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Create a board</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Board name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Assembly Line A"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Owner email</Label>
              <Input
                type="email"
                value={form.owner_email}
                onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                placeholder="owner@company.com"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Tier</Label>
              <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="mt-1.5"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#0090D8] hover:bg-[#0079B5] text-white"
              >
                {submitting ? 'Creating...' : 'Create board'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmptyBoard;
